import { Callout, EmptyState } from '@/components/UI';
import { getRuleRoleClassName, getRuleRoleLabel } from './helpers';
import type { ScorecardFeatureRow, ScorecardPreviewRow } from './types';

interface PreviewEntryDetailsProps {
    row: ScorecardPreviewRow;
    registryFeatureByKey: Map<string, ScorecardFeatureRow>;
}

// Helper to format values for display
const formatValue = (val: unknown): string => {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'true' : 'false';
    if (typeof val === 'number') return val.toFixed(2);
    if (typeof val === 'string') return val;
    if (Array.isArray(val)) return JSON.stringify(val);
    if (typeof val === 'object') return JSON.stringify(val);
    // Fallback for primitive types (symbol, bigint, etc.)
    return JSON.stringify(val);
};

// Helper to format selector for display
const formatSelector = (selector: unknown): string => {
    if (!selector) return '—';

    // Handle value selectors with array of acceptable values
    if (typeof selector === 'object' && 'values' in selector) {
        const s = selector as { values: unknown[] };

        if (Array.isArray(s.values)) {
            // If single value, show it directly; if multiple, show as array
            if (s.values.length === 1) {
                return formatValue(s.values[0]);
            }

            return s.values.map(formatValue).join(', ');
        }
    }

    if (typeof selector === 'object' && 'min' in selector && 'max' in selector) {
        const s = selector as { min: number; max: number };
        return `${s.min} - ${s.max}`;
    }

    if (typeof selector === 'object' && 'operator' in selector && 'value' in selector) {
        const s = selector as { operator: string; value: number };
        return `${s.operator} ${s.value}`;
    }

    return JSON.stringify(selector);
};

const PreviewEntryDetails = ({ row, registryFeatureByKey }: PreviewEntryDetailsProps) => {
    const { ruleEvaluations, rejectionReason, vetoTriggers } = row;

    return (
        <div className="p-2 overflow-x-auto">
            {row.beyondTrainingCutoff && (
                <Callout className="mb-3" title="Walk-forward / OOS" variant="warning">
                    This entry was scored after the baseline experiment training window.
                </Callout>
            )}
            {row.entityNotInExperiment && (
                <Callout className="mb-3" title="No eligible baseline" variant="warning">
                    This entry could not use an entity-specific or permitted fallback baseline at its timestamp, so it was rejected by
                    baseline policy.
                </Callout>
            )}
            {rejectionReason && (
                <Callout className="mb-3" title="Rejection Reason" variant="error">
                    <div>{rejectionReason}</div>
                    {vetoTriggers && vetoTriggers.length > 0 && (
                        <div className="mt-2">
                            <div className="text-red-400 text-xs mb-1">Veto Triggers:</div>
                            {vetoTriggers.map((trigger, idx) => (
                                <div className="text-zinc-400 text-xs ml-2" key={idx}>
                                    • Rule #{trigger.ruleId} ({trigger.type === 'direct' ? 'Direct Veto' : 'Threshold Veto'}):{' '}
                                    {trigger.reason}
                                </div>
                            ))}
                        </div>
                    )}
                </Callout>
            )}
            {ruleEvaluations.length === 0 ? (
                <EmptyState size="sm" title="No rule evaluations" />
            ) : (
                <table className="min-w-full text-xs">
                    <thead>
                        <tr className="text-zinc-300">
                            <th className="text-left p-2">Rule ID</th>
                            <th className="text-left p-2">Role</th>
                            <th className="text-left p-2">Feature</th>
                            <th className="text-left p-2">Resolved Value</th>
                            <th className="text-left p-2">Expected</th>
                            <th className="text-left p-2">Match</th>
                            <th className="text-left p-2">Lift</th>
                            <th className="text-left p-2">Contribution</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ruleEvaluations.map((r, i) => {
                            const registryLabel = r.feature.featureKey ? registryFeatureByKey.get(r.feature.featureKey)?.label : null;

                            const primaryFeatureLabel = registryLabel ?? r.feature.label ?? r.feature.featureKey;

                            const secondaryFeatureLabel =
                                r.feature.label && r.feature.label !== primaryFeatureLabel ? r.feature.label : null;

                            const matchClass = r.matched ? 'bg-green-950/30 border-green-800/50' : 'bg-red-950/20 border-red-800/30';

                            return (
                                <tr className={`border-t text-zinc-200 ${matchClass}`} key={i}>
                                    <td className="p-2 whitespace-nowrap">#{r.ruleId}</td>
                                    <td className="p-2">
                                        <span
                                            className={`inline-flex rounded border px-2 py-0.5 text-[11px] ${getRuleRoleClassName(r.ruleRole)}`}
                                        >
                                            {getRuleRoleLabel(r.ruleRole)}
                                        </span>
                                    </td>
                                    <td className="p-2 font-mono text-xs">
                                        <div>{primaryFeatureLabel}</div>
                                        {secondaryFeatureLabel && <div className="text-zinc-500">{secondaryFeatureLabel}</div>}
                                    </td>
                                    <td className="p-2 font-mono">
                                        <span className="text-blue-300">{formatValue(r.resolvedValue)}</span>
                                    </td>
                                    <td className="p-2 font-mono">
                                        <span className="text-amber-300">{formatSelector(r.selector)}</span>
                                    </td>
                                    <td className="p-2">
                                        <span className={r.matched ? 'text-green-400' : 'text-red-400'}>{r.matched ? '✓' : '✗'}</span>
                                    </td>
                                    <td className="p-2">{r.lift != null ? `${r.lift >= 0 ? '+' : ''}${r.lift.toFixed(2)}%` : '—'}</td>
                                    <td className="p-2">{r.contribution != null ? r.contribution.toFixed(4) : '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default PreviewEntryDetails;
